<script setup>
// 视频工具（nuxt#19 P3）。复刻 design videotool.js + 各单页：上传 → <video> 预览 + 轻量端内编辑
// （倍速 playbackRate / 静音 muted / 裁剪范围 / 裁切框 / 字幕叠加 / 抽帧 canvas 截图 / GIF 帧管理实时预览），
// 真转码（trim/crop/resize/reverse/merge/subtitle 的重新编码）端内做不了 → 不引 ffmpeg，给「Process with SharkFoto (server)」占位按钮。
// 抽帧(extract-video-frames)与 GIF 帧管理是真能端内做的轻活，已实做：canvas 截帧 + 逐张下载 / GIF 帧拖拽重排 + 实时循环预览。
import { ref, reactive, computed, watch, onBeforeUnmount, nextTick } from "vue";
import { perFileMax } from "@/settings/free-tools/batch-limits.js";
import { pollJob, jobWaitMsg } from "@/composables/useJobPoll.js";

const props = defineProps({
  mode: { type: String, default: "trim-video" }, // 见下方 modes
  slug: { type: String, default: "" },
});

// 哪些模式用「图片帧」输入（GIF Maker），其余用「视频」输入
const isGif = computed(() => props.mode === "gif-maker");
// 抽帧模式：可端内真抽（canvas）
const isFrames = computed(() => props.mode === "extract-video-frames");
const isMerge = computed(() => props.mode === "merge-videos");
const multi = computed(() => isMerge.value || isGif.value);

const TITLES = {
  "trim-video": "Trim & crop",
  "crop-video": "Crop & trim",
  "resize-video": "Resize video",
  "change-video-speed": "Change speed",
  "reverse-video": "Reverse video",
  "mute-video": "Mute video",
  "merge-videos": "Merge videos",
  "extract-video-frames": "Extract frames",
  "add-subtitles-to-video": "Add subtitles",
  "gif-maker": "GIF maker",
};
const exportLabel = computed(() => {
  if (isFrames.value) return "Extract frames";
  if (isGif.value) return "Create GIF";
  return "Export video";
});

// 主按钮文案：服务端处理中 → Processing…；轻活抽帧 → Extracting…；否则 → 各模式导出标签。
const primaryLabel = computed(() => {
  if (serverBusy.value) return "Processing on our servers…";
  return exportLabel.value;
});
// 主按钮禁用：抽帧中 或 服务端处理中。
const primaryDisabled = computed(() => extracting.value || serverBusy.value);

/* ============================ 共享状态 ============================ */
const stage = ref("drop"); // drop | work
const dragging = ref(false);
const fileInput = ref(null);
const toast = ref("");
let toastTimer = null;
function showToast(msg) {
  toast.value = msg;
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => (toast.value = ""), 2800);
}

/* ============================ 服务端重转码（异步 op 端点） ============================ */
// 重活（trim/crop/resize/speed/reverse/mute/subtitle、video→gif）端内做不了 → POST 原始字节到
// /api/free-tools/convert?op=… → Worker 对视频/音频走异步：返回 202 {jobId}，轮询 /job/<id> 到 done，
// 再 GET /result/<id> 取产物字节。此流程逐字复刻自 ToolStagePlaceholder.vue 的 onConvert 202 分支。
const serverBusy = ref(false); // 轮询中（"Processing on our servers…"）
const resultBlob = ref(null); // 产物 Blob（手动下载用）
const resultExt = ref(""); // 产物扩展名
const resultSize = ref(""); // 产物大小（人类可读）
const resultShown = ref(false); // 产物就绪（Download 可点）
const resultJobId = ref(""); // 后端异步产物 jobId(下载走 R2 公开 URL,不取 blob,与转换器 v3 一致)
const { downloadBlob: dlBlobViaPage, downloadUrl, resultUrl } = useFreeToolsDownload();

function extOf(file) {
  return (file && file.name && (file.name.match(/\.([a-z0-9]+)$/i) || [, ""])[1].toLowerCase()) || "mp4";
}
function baseNameOf(file) {
  return (file && file.name && file.name.replace(/\.[^.]+$/, "")) || "video";
}
function clearServerResult() {
  resultShown.value = false;
  resultBlob.value = null;
  resultJobId.value = "";
  resultExt.value = "";
  resultSize.value = "";
  if (lastDownloadUrl) { URL.revokeObjectURL(lastDownloadUrl); lastDownloadUrl = null; }
}

// 用源文件原始字节 POST 到异步转换端点，op + 参数由调用方拼到 qs；成功后填充 result* 并启用下载。
// toFormat 缺省=源扩展名（同格式重转码）；fromFormat 缺省=源扩展名。
async function runServerOp(file, qs, opts = {}) {
  if (!import.meta.client || !file || serverBusy.value) return;
  const from = extOf(file);
  const to = opts.toFormat || from;
  serverBusy.value = true;
  clearServerResult();
  try {
    const full =
      "to_format=" + encodeURIComponent(to) +
      "&from_format=" + encodeURIComponent(from) +
      "&dl_name=" + encodeURIComponent(baseNameOf(file) + "." + to) +
      (qs ? "&" + qs : "");
    const res = await fetch("/api/free-tools/convert?" + full, {
      method: "POST",
      headers: { "Content-Type": "application/octet-stream" },
      body: await file.arrayBuffer(),
    });
    // 异步重负载（202 + jobId）：轮询 /job 到 done，再取 /result。
    if (res.status === 202) {
      const { jobId } = await res.json();
      // 退避 + 长上限轮询,覆盖后端重转码真实耗时(见 useJobPoll);升级"仍在处理"提示。
      const job = await pollJob(jobId, { onTick: (_j, ms) => { const m = jobWaitMsg(ms); if (m) showToast(m); } });
      if (!job || job.status !== "done") {
        showToast((job && job.detail) || "Processing timed out. Please try again or use a smaller file.");
        return;
      }
      const ext = (job.ext || to) === "jpeg" ? "jpg" : job.ext || to;
      // 后端产物已在 R2:下载走公开 URL(不取 blob、不经 Nitro 代理、任意大小,与转换器 v3 一致)。
      resultJobId.value = jobId;
      resultExt.value = ext;
      resultSize.value = fmtBytes(Number(job.size) || 0);
      resultShown.value = true;
      showToast("Done — your file is ready to download.");
      return;
    }
    // 非 202：直接成功（同步产物）或错误。
    if (!res.ok) {
      let detail = "";
      try { const j = await res.json(); detail = j && j.detail ? j.detail : ""; } catch (_) { /* not JSON */ }
      showToast(detail || "Processing failed (" + res.status + "). Please try again.");
      return;
    }
    const blob = await res.blob();
    const ext = to === "jpeg" ? "jpg" : to;
    resultBlob.value = blob;
    resultExt.value = ext;
    resultSize.value = fmtBytes(blob.size);
    resultShown.value = true;
    showToast("Done — your file is ready to download.");
  } catch (err) {
    showToast("Could not reach the processing server. Please try again.");
  } finally {
    serverBusy.value = false;
  }
}

// merge-videos：把 N(>=2) 个 File 以 multipart/form-data（字段名 "files"）POST 到 /api/free-tools/merge，
// 202→轮询 /job→取 /result 的逻辑逐字复用 runServerOp（toFormat 视作 "mp4"）。不要手动设 Content-Type。
async function runMerge() {
  if (!import.meta.client || serverBusy.value || clips.value.length < 2) {
    if (clips.value.length < 2) showToast("Add at least 2 clips to merge.");
    return;
  }
  const to = "mp4";
  serverBusy.value = true;
  clearServerResult();
  try {
    const fd = new FormData();
    clips.value.forEach((c) => fd.append("files", c.file));
    const res = await fetch("/api/free-tools/merge?to_format=mp4", { method: "POST", body: fd });
    // 异步重负载（202 + jobId）：轮询 /job 到 done，再取 /result。
    if (res.status === 202) {
      const { jobId } = await res.json();
      // 退避 + 长上限轮询,覆盖后端重转码真实耗时(见 useJobPoll);升级"仍在处理"提示。
      const job = await pollJob(jobId, { onTick: (_j, ms) => { const m = jobWaitMsg(ms); if (m) showToast(m); } });
      if (!job || job.status !== "done") {
        showToast((job && job.detail) || "Processing timed out. Please try again or use a smaller file.");
        return;
      }
      const ext = (job.ext || to) === "jpeg" ? "jpg" : job.ext || to;
      // 后端产物已在 R2:下载走公开 URL(不取 blob、不经 Nitro 代理、任意大小,与转换器 v3 一致)。
      resultJobId.value = jobId;
      resultExt.value = ext;
      resultSize.value = fmtBytes(Number(job.size) || 0);
      resultShown.value = true;
      showToast("Done — your merged video is ready to download.");
      return;
    }
    // 非 202：直接成功（同步产物）或错误。
    if (!res.ok) {
      let detail = "";
      try { const j = await res.json(); detail = j && j.detail ? j.detail : ""; } catch (_) { /* not JSON */ }
      showToast(detail || "Processing failed (" + res.status + "). Please try again.");
      return;
    }
    const blob = await res.blob();
    const ext = to === "jpeg" ? "jpg" : to;
    resultBlob.value = blob;
    resultExt.value = ext;
    resultSize.value = fmtBytes(blob.size);
    resultShown.value = true;
    showToast("Done — your merged video is ready to download.");
  } catch (err) {
    showToast("Could not reach the processing server. Please try again.");
  } finally {
    serverBusy.value = false;
  }
}

function fmtBytes(b) {
  if (b < 1024) return b + " B";
  if (b < 1048576) return (b / 1024).toFixed(1) + " KB";
  return (b / 1048576).toFixed(2) + " MB";
}
let lastDownloadUrl = null; // 仅保留最近一个下载用 blob: URL，换新前先释放
function onDownload() {
  if (!import.meta.client) return;
  const filename = (clips.value[0] ? baseNameOf(clips.value[0].file) : "video") + "." + (resultExt.value || "mp4");
  // 后端异步产物 → R2 公开 URL 经 /download 导航下载(任意大小);同步产物 → blob 经 /download。(与转换器 v3 一致)
  if (resultJobId.value) return downloadUrl(resultUrl(resultJobId.value), filename, resultSize.value);
  if (resultBlob.value) return dlBlobViaPage(resultBlob.value, filename);
}

const objectUrls = new Set();
function mintUrl(file) { const u = URL.createObjectURL(file); objectUrls.add(u); return u; }
function revokeAll() { objectUrls.forEach((u) => URL.revokeObjectURL(u)); objectUrls.clear(); }

function pick() { fileInput.value && fileInput.value.click(); }
function onInput(e) {
  const list = e.target.files;
  if (list && list.length) loadFiles(list);
  if (fileInput.value) fileInput.value.value = "";
}
function onDrop(e) {
  e.preventDefault();
  dragging.value = false;
  const list = e.dataTransfer && e.dataTransfer.files;
  if (list && list.length) loadFiles(list);
}

function fmtTime(s) {
  s = Math.max(0, s || 0);
  const m = Math.floor(s / 60), ss = Math.floor(s % 60);
  return m + ":" + String(ss).padStart(2, "0");
}

/* ============================ 视频模式 ============================ */
const videoEl = ref(null);
const clips = ref([]); // {name,url,file}
const playing = ref(false);
const curTime = ref(0);
const duration = ref(0);
const vDims = ref(""); // 1280×720

// trim 范围（秒）
const trimStart = ref(0);
const trimEnd = ref(0);
// speed
const speed = ref(1);
const SPEED_PRESETS = [0.5, 1, 1.5, 2];
// mute / fade / keep-audio toggles
const muted = ref(props.mode === "mute-video");
const fade = ref(true);
const keepAudio = ref(false);
// crop ratio
const cropRatio = ref("free");
const CROP_RATIOS = ["free", "9:16", "1:1", "4:5", "16:9"];
// 裁剪矩形：0~1 比例，相对「视频真正显示的矩形」（不含 letterbox 黑边）。
// 由用户拖拽/缩放手柄驱动，导出时再 × 视频原生分辨率换成像素。
const cropRect = reactive({ x: 0.1, y: 0.1, w: 0.8, h: 0.8 });
const MIN_CROP = 0.08; // 最小裁剪边（占显示矩形比例），避免缩到 0
// resize
const resPreset = ref("1080");
const RES_PRESETS = [["1080", "1080p"], ["720", "720p"], ["480", "480p"], ["360", "360p"]];
// export format
const fmt = ref("mp4");
const FMTS = [["mp4", "MP4"], ["webm", "WEBM"], ["gif", "GIF"]];
// subtitle
const capText = ref("Your caption here");
const capPos = ref("bottom"); // bottom | center | top

const fileMeta = computed(() => {
  if (multi.value) return clips.value.length + " clip" + (clips.value.length > 1 ? "s" : "");
  return (vDims.value ? vDims.value + " · " : "") + fmtTime(duration.value);
});
const fileName = computed(() => {
  if (multi.value && clips.value.length > 1) return clips.value.length + " videos";
  return clips.value[0] ? clips.value[0].name : "";
});
const trimDur = computed(() => Math.max(0, trimEnd.value - trimStart.value));
const capStyle = computed(() => {
  if (capPos.value === "top") return { top: "7%", bottom: "auto" };
  if (capPos.value === "center") return { top: "46%", bottom: "auto" };
  return { top: "auto", bottom: "8%" };
});

function loadFiles(list) {
  // 尺寸上限(与转换器/批量同一来源 perFileMax):视频编辑同样卡 50MB(媒体容器仅 2 实例 + 转码慢,AE 审计 ③)。
  const _max = perFileMax("video");
  const _big = [...list].find((f) => f.size > _max);
  if (_big) { alert(`File is too large (${(_big.size / 1048576).toFixed(1)} MB). Max is ${Math.round(_max / 1048576)} MB for this tool.`); return; }
  if (isGif.value) { addGifFiles(list); return; }
  const vids = [...list].filter((f) => f.type.startsWith("video/"));
  if (!vids.length) { alert("Please choose a video file."); return; }
  const next = multi.value ? clips.value.concat(vids.map(wrap)) : [wrap(vids[0])];
  clips.value = next;
  stage.value = "work";
  nextTick(() => {
    const v = videoEl.value;
    const first = clips.value[0];
    if (v && first) {
      v.src = first.url;
      v.muted = muted.value;
      v.playbackRate = speed.value;
    }
  });
}
function wrap(file) { return { name: file.name, url: mintUrl(file), file }; }

function onLoadedMeta() {
  const v = videoEl.value; if (!v) return;
  duration.value = v.duration || 0;
  vDims.value = v.videoWidth && v.videoHeight ? v.videoWidth + "×" + v.videoHeight : "";
  if (!trimEnd.value || trimEnd.value > duration.value) trimEnd.value = duration.value;
  trimStart.value = Math.min(trimStart.value, duration.value);
  // 原生尺寸就绪后，按当前比例把裁剪框重设为显示矩形内最大内接居中（free=80%）。
  if (showCropOverlay.value) nextTick(() => reframeCrop());
  applyLive();
}
function onTimeUpdate() {
  const v = videoEl.value; if (!v) return;
  curTime.value = v.currentTime;
  // trim 模式：超出选区回到 start，形成「只播选段」预览
  if ((props.mode === "trim-video" || props.mode === "crop-video") && trimEnd.value > trimStart.value) {
    if (v.currentTime > trimEnd.value || v.currentTime < trimStart.value - 0.05) v.currentTime = trimStart.value;
  }
}
function togglePlay() {
  const v = videoEl.value; if (!v) return;
  v.paused ? v.play() : v.pause();
}
function applyLive() {
  const v = videoEl.value; if (!v) return;
  v.playbackRate = speed.value;
  v.muted = muted.value;
}
watch(speed, applyLive);
watch(muted, applyLive);

// 任一编辑参数改变 → 作废已就绪的服务端产物，主按钮回到「Export」（避免下到旧参数的文件）。
watch([trimStart, trimEnd, speed, muted, cropRatio, resPreset, fmt, capText, capPos, keepAudio], () => {
  if (resultShown.value) clearServerResult();
});

function setSpeed(s) { speed.value = s; }
function selectClip(i) {
  const v = videoEl.value, c = clips.value[i]; if (!v || !c) return;
  v.src = c.url; v.muted = muted.value; v.playbackRate = speed.value;
}
function removeClip(i) {
  clips.value.splice(i, 1);
  if (!clips.value.length) reset();
  else if (i === 0) selectClip(0);
}

/* ---- timeline / trim handles（拖拽 0–100% → 秒） ---- */
const stripEl = ref(null);
let dragHandle = null; // 'l' | 'r' | null
function pctToSec(clientX) {
  const el = stripEl.value; if (!el) return 0;
  const r = el.getBoundingClientRect();
  const p = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
  return p * (duration.value || 0);
}
function onHandleDown(which, e) { dragHandle = which; try { e.target.setPointerCapture(e.pointerId); } catch (_) {} }
function onStripMove(e) {
  if (!dragHandle) return;
  const sec = pctToSec(e.clientX);
  if (dragHandle === "l") trimStart.value = Math.min(sec, trimEnd.value - 0.1);
  else trimEnd.value = Math.max(sec, trimStart.value + 0.1);
  trimStart.value = Math.max(0, trimStart.value);
  trimEnd.value = Math.min(duration.value || sec, trimEnd.value);
}
function onStripUp() { dragHandle = null; }
const startPct = computed(() => (duration.value ? (trimStart.value / duration.value) * 100 : 0));
const endPct = computed(() => (duration.value ? (trimEnd.value / duration.value) * 100 : 100));
const playPct = computed(() => (duration.value ? (curTime.value / duration.value) * 100 : 0));

/* ============================ 裁剪框（crop-video，真可拖拽） ============================ */
// 复刻 CropTool.vue 的拖拽/手柄/比例范式，但要点不同：CropTool 的 crop-layer 与画布等大，
// 百分比 1:1 对应像素；这里 <video> 在 aspect-ratio:16/9 的 .vstage 里用 object-fit:contain，
// 视频原生宽高比 ≠ 16/9 时会出现 letterbox（上下或左右黑边）。所以 cropRect 用的是「视频真正
// 显示矩形」的归一化坐标，stageEl + videoEl 一起算出该显示矩形，再把 cropRect 映射成 stage 内的
// 百分比给 .crop-box 定位；导出时把 cropRect × 视频原生分辨率得到像素裁切框。
const stageEl = ref(null);
let dragCrop = null; // null | 'move' | 'tl' | 'tr' | 'bl' | 'br'
let cropAR = 0; // 锁定的宽高比（视频显示矩形坐标系里的 w/h）；0=free
let cropStart = null; // 拖拽起点快照

// 当前比例对应的目标「视频显示矩形内」宽高比（free=0）。
// 注意：cropRect 用归一化到「显示矩形」的坐标，要保持画面上的真实比例 rw:rh，
// 需把它换算成显示矩形坐标系：arRect = (rw/rh) / (dispW/dispH)。
function ratioAR() {
  if (cropRatio.value === "free") return 0;
  const [rw, rh] = cropRatio.value.split(":").map(Number);
  const d = videoDisp();
  if (!d || !d.w || !d.h) return 0;
  return (rw / rh) / (d.w / d.h);
}

// 计算视频在 .vstage 里真正显示的矩形（像素，相对 stage 左上角）。
// 用 videoWidth/Height 的宽高比与 stage 宽高比比较，推内接区域（object-fit:contain）。
// 取不到原生尺寸时退化为「填满 stage」（16:9 主路径）。
function videoDisp() {
  const st = stageEl.value;
  if (!st) return null;
  const sw = st.clientWidth, sh = st.clientHeight;
  if (!sw || !sh) return null;
  const v = videoEl.value;
  const vw = (v && v.videoWidth) || 0;
  const vh = (v && v.videoHeight) || 0;
  if (!vw || !vh) return { x: 0, y: 0, w: sw, h: sh }; // 近似：无原生尺寸 → 填满
  const ar = vw / vh, sar = sw / sh;
  let w, h;
  if (ar > sar) { w = sw; h = sw / ar; } // 视频更宽 → 左右填满，上下黑边
  else { h = sh; w = sh * ar; }          // 视频更高 → 上下填满，左右黑边
  return { x: (sw - w) / 2, y: (sh - h) / 2, w, h };
}

// cropRect(归一化于显示矩形) → .crop-box 的 stage 内百分比定位（含 letterbox 偏移）。
const cropBoxStyle = computed(() => {
  // 依赖 vDims 使其在元数据就绪后重算（videoDisp 读 DOM，非响应式）
  void vDims.value;
  const st = stageEl.value;
  const d = videoDisp();
  if (!st || !d) {
    // 元数据未就绪时给个居中占位
    return { left: "10%", top: "10%", width: "80%", height: "80%" };
  }
  const sw = st.clientWidth || 1, sh = st.clientHeight || 1;
  const left = (d.x + cropRect.x * d.w) / sw * 100;
  const top = (d.y + cropRect.y * d.h) / sh * 100;
  const width = (cropRect.w * d.w) / sw * 100;
  const height = (cropRect.h * d.h) / sh * 100;
  return { left: left + "%", top: top + "%", width: width + "%", height: height + "%" };
});

const cropReadout = computed(() => {
  const v = videoEl.value;
  const W = (v && v.videoWidth) || 0, H = (v && v.videoHeight) || 0;
  if (!W || !H) return "Drag the box to set the crop";
  const w = Math.round(cropRect.w * W), h = Math.round(cropRect.h * H);
  return w + " × " + h + " px";
});

function clamp01(n) { return Math.max(0, Math.min(1, n)); }

// 按当前比例把 cropRect 重设为「显示矩形内最大内接、居中」（free 用 80% 居中）。
function reframeCrop() {
  const ar = ratioAR();
  cropAR = ar;
  if (!ar) {
    // free：80% 居中
    cropRect.w = 0.8; cropRect.h = 0.8; cropRect.x = 0.1; cropRect.y = 0.1;
    return;
  }
  // 最大内接 ar（cropRect 坐标系，整个显示矩形是 1×1）
  let w = 1, h = w / ar;
  if (h > 1) { h = 1; w = h * ar; }
  cropRect.w = w; cropRect.h = h;
  cropRect.x = (1 - w) / 2; cropRect.y = (1 - h) / 2;
}
watch(cropRatio, reframeCrop);

// 平移整框（夹在显示矩形 0~1 内）
function onCropBoxDown(e) {
  dragCrop = "move";
  cropStart = { px: e.clientX, py: e.clientY, x: cropRect.x, y: cropRect.y, w: cropRect.w, h: cropRect.h };
  try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
}
// 从某角缩放（夹紧、保持最小尺寸；非 free 锁比例）
function onCropHandleDown(which, e) {
  e.stopPropagation(); // 不要触发 box 的平移
  dragCrop = which;
  cropStart = { px: e.clientX, py: e.clientY, x: cropRect.x, y: cropRect.y, w: cropRect.w, h: cropRect.h };
  try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
}

function onCropMove(e) {
  if (!dragCrop || !cropStart) return;
  const d = videoDisp();
  if (!d || !d.w || !d.h) return;
  // 指针位移换算成「显示矩形」归一化增量
  const dx = (e.clientX - cropStart.px) / d.w;
  const dy = (e.clientY - cropStart.py) / d.h;

  if (dragCrop === "move") {
    cropRect.x = Math.max(0, Math.min(1 - cropStart.w, cropStart.x + dx));
    cropRect.y = Math.max(0, Math.min(1 - cropStart.h, cropStart.y + dy));
    return;
  }

  // 缩放：确定被拖动的角（右/下）。固定对角点不动。
  const right = dragCrop === "tr" || dragCrop === "br";
  const bottom = dragCrop === "bl" || dragCrop === "br";
  const sx = cropStart.x, sy = cropStart.y, sw = cropStart.w, sh = cropStart.h;
  const fixedRight = sx + sw;   // 当拖左边角时固定的右沿
  const fixedBottom = sy + sh;  // 当拖上边角时固定的下沿

  if (cropAR) {
    // 锁比例：以宽度为主驱动，宽度由水平指针决定，高度按 cropAR 推。
    let nw;
    if (right) nw = clamp01(sx + sw + dx) - sx; // 右沿跟指针，左沿 sx 固定
    else nw = (sx + sw) - clamp01(sx + dx);     // 左沿跟指针，右沿 fixedRight 固定
    nw = Math.max(MIN_CROP, nw);
    let nh = nw / cropAR;
    // 限制不超出显示矩形
    const maxW = right ? 1 - sx : fixedRight;
    const maxH = bottom ? 1 - sy : fixedBottom;
    if (nw > maxW) { nw = maxW; nh = nw / cropAR; }
    if (nh > maxH) { nh = maxH; nw = nh * cropAR; }
    nw = Math.max(MIN_CROP, nw); nh = Math.max(MIN_CROP / cropAR, nh);
    cropRect.w = nw; cropRect.h = nh;
    cropRect.x = right ? sx : fixedRight - nw;
    cropRect.y = bottom ? sy : fixedBottom - nh;
  } else {
    // free：横竖独立
    let nl = sx, nt = sy, nw = sw, nh = sh;
    if (right) nw = Math.max(MIN_CROP, Math.min(1 - sx, sw + dx));
    else { nl = Math.min(sx + sw - MIN_CROP, Math.max(0, sx + dx)); nw = sw + (sx - nl); }
    if (bottom) nh = Math.max(MIN_CROP, Math.min(1 - sy, sh + dy));
    else { nt = Math.min(sy + sh - MIN_CROP, Math.max(0, sy + dy)); nh = sh + (sy - nt); }
    cropRect.x = nl; cropRect.y = nt; cropRect.w = nw; cropRect.h = nh;
  }
}
function onCropUp() { dragCrop = null; cropStart = null; }

/* ============================ 抽帧（真端内） ============================ */
const frameInterval = ref(1); // 秒
const frameFmt = ref("png"); // png | jpg
const extracting = ref(false);
async function extractFrames() {
  const v = videoEl.value; if (!v || !duration.value) return;
  extracting.value = true;
  try {
    const c = document.createElement("canvas");
    c.width = v.videoWidth || 1280; c.height = v.videoHeight || 720;
    const ctx = c.getContext("2d");
    const mime = frameFmt.value === "jpg" ? "image/jpeg" : "image/png";
    const ext = frameFmt.value === "jpg" ? "jpg" : "png";
    const wasMuted = v.muted; v.pause(); v.muted = true;
    let n = 0;
    for (let t = 0; t < duration.value && n < 60; t += frameInterval.value) {
      await seek(v, t);
      ctx.drawImage(v, 0, 0, c.width, c.height);
      const blob = await new Promise((r) => c.toBlob(r, mime, 0.92));
      if (blob) { downloadBlob(blob, "frame-" + String(n + 1).padStart(3, "0") + "." + ext); n++; }
    }
    v.muted = wasMuted;
    showToast(n + " frame" + (n === 1 ? "" : "s") + " saved (every " + frameInterval.value + "s)");
  } catch (e) {
    showToast("Could not read frames from this video.");
  } finally {
    extracting.value = false;
  }
}
function seek(v, t) {
  return new Promise((resolve) => {
    const onSeek = () => { v.removeEventListener("seeked", onSeek); resolve(); };
    v.addEventListener("seeked", onSeek);
    v.currentTime = Math.min(t, (v.duration || t) - 0.01);
  });
}

/* ============================ GIF maker（真帧管理 + 实时预览） ============================ */
const gframes = ref([]); // {url,name}
const gfps = ref(8);
const gsize = ref("240");
const gdir = ref("fwd"); // fwd | boomerang
const gloop = ref(true);
const gifFrameUrl = ref("");
let gifIdx = 0, gifDir = 1, gifTimer = null, dragFrom = null;
const GIF_SIZES = [["240", "240px"], ["480", "480px"], ["720", "720px"]];

function addGifFiles(list) {
  [...list].filter((f) => f.type.startsWith("image/")).forEach((f) => gframes.value.push({ url: mintUrl(f), name: f.name }));
  if (gframes.value.length) { stage.value = "work"; gifIdx = 0; gifDir = 1; gifPlay(); }
}
function gifPlay() {
  if (gifTimer) clearInterval(gifTimer);
  if (gframes.value.length < 1) return;
  gifTimer = setInterval(() => {
    gifFrameUrl.value = gframes.value[gifIdx] ? gframes.value[gifIdx].url : "";
    if (gdir.value === "boomerang") {
      gifIdx += gifDir;
      if (gifIdx >= gframes.value.length - 1) { gifIdx = gframes.value.length - 1; gifDir = -1; }
      else if (gifIdx <= 0) { gifIdx = 0; gifDir = 1; }
    } else {
      gifIdx = (gifIdx + 1) % gframes.value.length;
    }
  }, 1000 / gfps.value);
}
watch([gfps, gdir], () => { gifIdx = 0; gifDir = 1; gifPlay(); });
function removeFrame(i) {
  gframes.value.splice(i, 1); gifIdx = 0;
  if (!gframes.value.length) reset(); else gifPlay();
}
function onFrameDragStart(i) { dragFrom = i; }
function onFrameDrop(i) {
  if (dragFrom === null) return;
  const m = gframes.value.splice(dragFrom, 1)[0];
  gframes.value.splice(i, 0, m);
  dragFrom = null; gifIdx = 0; gifPlay();
}

/* ============================ 导出（轻活真做 / 重活占位） ============================ */
function downloadBlob(blob, name) {
  const a = document.createElement("a");
  const url = URL.createObjectURL(blob);
  a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
// 用用户拖出的 cropRect(归一化于视频显示矩形=归一化于原生帧) × 视频原生分辨率，
// 推像素裁切框 w:h:x:y。偶数化并夹在帧内，避免编码器报错 / 越界。
function cropBoxPx() {
  const v = videoEl.value;
  const W = (v && v.videoWidth) || 0;
  const H = (v && v.videoHeight) || 0;
  if (!W || !H) return "";
  let w = Math.round(cropRect.w * W);
  let h = Math.round(cropRect.h * H);
  let x = Math.round(cropRect.x * W);
  let y = Math.round(cropRect.y * H);
  // 偶数化，规避编码器对奇数尺寸的报错
  w -= w % 2; h -= h % 2;
  // 夹在帧内
  w = Math.max(2, Math.min(W, w));
  h = Math.max(2, Math.min(H, h));
  x = Math.max(0, Math.min(W - w, x));
  y = Math.max(0, Math.min(H - h, y));
  return w + ":" + h + ":" + x + ":" + y;
}

function exportAction() {
  if (isFrames.value) { extractFrames(); return; } // 端内真抽帧，保持不变
  if (isGif.value) {
    // GIF maker：图片帧 → GIF（端内合成代价高，无 ffmpeg）→ 暂保留占位（多输入未支持）。
    // 端内已给实时循环预览；最终 GIF 编码后端 multi-input 上线后再接。
    if (gframes.value.length < 1) return;
    showToast("Send " + gframes.value.length + " frames · " + gfps.value + " fps · " + gsize.value + "px to SharkFoto to encode your GIF.");
    return;
  }
  if (isMerge.value) {
    // merge-videos：multipart 上传 N 个 clip → /api/free-tools/merge（<2 clips 守卫在 runMerge 内）。
    runMerge();
    return;
  }

  const file = clips.value[0] && clips.value[0].file;
  if (!file) return;

  // 各模式 → op + 参数，POST 原始字节走异步端点（to_format 默认=源扩展名）。
  switch (props.mode) {
    case "trim-video": {
      const start = Math.max(0, trimStart.value);
      const dur = Math.max(0.1, trimEnd.value - trimStart.value);
      runServerOp(file, "op=trim&start=" + start.toFixed(2) + "&duration=" + dur.toFixed(2));
      break;
    }
    case "crop-video": {
      const box = cropBoxPx();
      if (!box) { showToast("Could not read video dimensions. Try reloading the clip."); return; }
      runServerOp(file, "op=crop&crop=" + encodeURIComponent(box));
      break;
    }
    case "resize-video": {
      // 高度档位 → scale=w:-1 等比（宽自适应；高=档位）。
      runServerOp(file, "op=resize&scale=" + encodeURIComponent("-1:" + resPreset.value));
      break;
    }
    case "change-video-speed": {
      const f = Math.min(4, Math.max(0.25, speed.value || 1));
      runServerOp(file, "op=speed&speed=" + f);
      break;
    }
    case "reverse-video": {
      runServerOp(file, "op=reverse");
      break;
    }
    case "mute-video": {
      runServerOp(file, "op=mute");
      break;
    }
    case "add-subtitles-to-video": {
      runServerOp(file, "op=subtitle&text=" + encodeURIComponent(capText.value || "") + "&pos=" + encodeURIComponent(capPos.value));
      break;
    }
    default:
      showToast("This action isn’t available yet.");
  }
}

function reset() {
  const v = videoEl.value;
  if (v) { v.pause(); v.removeAttribute("src"); v.load && v.load(); }
  if (gifTimer) clearInterval(gifTimer);
  revokeAll();
  clearServerResult();
  serverBusy.value = false;
  stage.value = "drop";
  clips.value = []; gframes.value = []; gifFrameUrl.value = "";
  duration.value = 0; curTime.value = 0; vDims.value = "";
  trimStart.value = 0; trimEnd.value = 0; playing.value = false;
  cropRatio.value = "free";
  cropRect.x = 0.1; cropRect.y = 0.1; cropRect.w = 0.8; cropRect.h = 0.8;
  if (fileInput.value) fileInput.value.value = "";
}

onBeforeUnmount(() => {
  if (gifTimer) clearInterval(gifTimer);
  if (toastTimer) clearTimeout(toastTimer);
  if (lastDownloadUrl) { URL.revokeObjectURL(lastDownloadUrl); lastDownloadUrl = null; }
  revokeAll();
});

/* 各模式显示哪些控件 */
const showTrim = computed(() => props.mode === "trim-video" || props.mode === "crop-video");
const showCrop = computed(() => props.mode === "crop-video");
const showCropOverlay = computed(() => props.mode === "crop-video");
const showSpeed = computed(() => props.mode === "change-video-speed");
const showMuteSw = computed(() => props.mode === "mute-video" || props.mode === "trim-video");
const showFadeSw = computed(() => props.mode === "trim-video");
const showResize = computed(() => props.mode === "resize-video");
const showReverse = computed(() => props.mode === "reverse-video");
const showSubtitle = computed(() => props.mode === "add-subtitles-to-video");
const showFmtSeg = computed(() => props.mode === "trim-video" || props.mode === "crop-video");
const dzFormatList = computed(() => (isGif.value ? ["PNG", "JPG", "WEBP", "2–100 frames"] : ["MP4", "MOV", "WEBM", "AVI"]));
const dzHeading = computed(() => (isGif.value ? "Drop 2 or more images" : multi.value ? "Drop your videos here" : "Drop your video here"));
const dzHint = computed(() => (isGif.value ? "or click to browse — they become GIF frames" : "or click to browse — large files are fine"));
const accept = computed(() => (isGif.value ? "image/*" : "video/*"));
</script>

<template>
  <div class="vt" @pointermove="onStripMove" @pointerup="onStripUp">
    <input ref="fileInput" type="file" :accept="accept" :multiple="multi" hidden @change="onInput" />

    <!-- 拖放区 -->
    <div v-show="stage === 'drop'" class="dz" :class="{ drag: dragging }"
      @click="pick" @dragenter.prevent="dragging = true" @dragover.prevent="dragging = true"
      @dragleave.prevent="dragging = false" @drop="onDrop">
      <div class="dz-ico">
        <svg v-if="isGif" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="3" /><path d="M7 10v4M11 10v4M15 10h2M15 12h1.5M15 14v-4" /></svg>
        <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="3" /><path d="M10 9l5 3-5 3z" fill="currentColor" stroke="none" /></svg>
      </div>
      <h3>{{ dzHeading }}</h3>
      <div class="hint">{{ dzHint }}</div>
      <div class="formats"><span v-for="f in dzFormatList" :key="f">{{ f }}</span></div>
      <div class="dz-or">Processed securely — never stored</div>
      <button class="btn btn-vid btn-lg" type="button" @click.stop="pick">{{ isGif ? "Choose images" : "Choose video" }}</button>
    </div>

    <!-- 工作区 -->
    <div v-if="stage === 'work'" class="ws">
      <div class="ws-grid">
        <!-- 左：预览 -->
        <div class="ws-left">
          <!-- GIF 实时预览 -->
          <template v-if="isGif">
            <div class="gif-stage">
              <span class="live-badge"><span class="dot"></span>LIVE PREVIEW</span>
              <img v-if="gifFrameUrl" :src="gifFrameUrl" class="gif-frame" alt="preview" />
            </div>
            <div class="frames-bar">
              <div class="frames-lab"><span>{{ gframes.length }} frame{{ gframes.length === 1 ? "" : "s" }}</span><a class="clear" @click="reset">Clear all</a></div>
              <div class="frames">
                <div v-for="(f, i) in gframes" :key="f.url" class="frame-thumb" :class="{ cur: i === gifIdx }" draggable="true"
                  @dragstart="onFrameDragStart(i)" @dragover.prevent @drop="onFrameDrop(i)">
                  <img :src="f.url" alt="" />
                  <span class="idx">{{ i + 1 }}</span>
                  <span class="del" @click.stop="removeFrame(i)"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg></span>
                </div>
                <div class="frame-add" @click="pick"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14" stroke-linecap="round" /></svg></div>
              </div>
            </div>
          </template>

          <!-- 视频预览 -->
          <template v-else>
            <div ref="stageEl" class="vstage" @pointermove="onCropMove" @pointerup="onCropUp" @pointercancel="onCropUp">
              <video ref="videoEl" class="video" playsinline @loadedmetadata="onLoadedMeta" @timeupdate="onTimeUpdate"
                @play="playing = true" @pause="playing = false"></video>
              <!-- 裁切框（仅 crop）：暗层 pointer-events:none，box/手柄 pointer-events:auto，:style 由 cropRect 驱动 -->
              <div v-if="showCropOverlay" class="crop-ov">
                <div class="crop-box" :style="cropBoxStyle" @pointerdown="onCropBoxDown">
                  <span class="hd tl" @pointerdown="onCropHandleDown('tl', $event)" />
                  <span class="hd tr" @pointerdown="onCropHandleDown('tr', $event)" />
                  <span class="hd bl" @pointerdown="onCropHandleDown('bl', $event)" />
                  <span class="hd br" @pointerdown="onCropHandleDown('br', $event)" />
                </div>
              </div>
              <!-- 字幕叠加 -->
              <div v-if="showSubtitle && capText" class="cap-overlay" :style="capStyle"><span class="cap">{{ capText }}</span></div>
            </div>

            <div class="transport">
              <button class="tp-btn" type="button" @click="togglePlay">
                <svg v-if="playing" viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>
                <svg v-else viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
              </button>
              <div class="tp-time"><b>{{ fmtTime(curTime) }}</b> / <span>{{ fmtTime(duration) }}</span></div>
              <div style="flex:1"></div>
              <div v-if="showTrim" class="tp-sel">Trim: {{ fmtTime(trimStart) }} – {{ fmtTime(trimEnd) }}</div>
            </div>

            <!-- trim 时间轴 -->
            <div v-if="showTrim" class="timeline">
              <div class="tl-label"><span>Trim range</span><span>{{ trimDur.toFixed(1) }}s selected</span></div>
              <div ref="stripEl" class="filmstrip">
                <div class="tl-mask" :style="{ width: startPct + '%' }"></div>
                <div class="tl-mask r" :style="{ width: 100 - endPct + '%' }"></div>
                <div class="tl-range" :style="{ left: startPct + '%', right: 100 - endPct + '%' }">
                  <div class="tl-handle l" @pointerdown="onHandleDown('l', $event)"></div>
                  <div class="tl-handle r" @pointerdown="onHandleDown('r', $event)"></div>
                </div>
                <div class="tl-playhead" :style="{ left: playPct + '%' }"></div>
              </div>
              <div class="tl-times"><span>0:00</span><span>{{ fmtTime(duration) }}</span></div>
            </div>
          </template>
        </div>

        <!-- 右：控制面板 -->
        <div class="ws-panel">
          <div class="ws-file">
            <div class="thumb">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="5" width="18" height="14" rx="3" /><path d="M10 9l5 3-5 3z" fill="currentColor" stroke="none" /></svg>
            </div>
            <div class="meta"><div class="fn">{{ fileName }}</div><div class="fsz">{{ fileMeta }}</div></div>
            <button class="rm" type="button" title="Remove" @click="reset"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg></button>
          </div>

          <!-- trim 控件 -->
          <template v-if="showTrim">
            <div class="ctl"><label>Start</label><div class="field"><input type="text" :value="fmtTime(trimStart)" readonly /><span class="unit">mm:ss</span></div></div>
            <div class="ctl"><label>End</label><div class="field"><input type="text" :value="fmtTime(trimEnd)" readonly /><span class="unit">mm:ss</span></div></div>
          </template>

          <!-- crop 比例 -->
          <div v-if="showCrop" class="ctl">
            <label>Aspect ratio</label>
            <div class="seg">
              <button v-for="r in CROP_RATIOS" :key="r" type="button" :class="{ active: cropRatio === r }" @click="cropRatio = r">{{ r === "free" ? "Free" : r }}</button>
            </div>
            <div class="crop-read">{{ cropReadout }}</div>
          </div>

          <!-- speed -->
          <template v-if="showSpeed">
            <div class="ctl">
              <label>Speed <span class="rangeval">{{ speed }}×</span></label>
              <div class="rangewrap"><input type="range" min="0.25" max="4" step="0.25" v-model.number="speed" /></div>
            </div>
            <div class="seg">
              <button v-for="s in SPEED_PRESETS" :key="s" type="button" :class="{ active: speed === s }" @click="setSpeed(s)">{{ s }}×</button>
            </div>
          </template>

          <!-- resize -->
          <div v-if="showResize" class="ctl">
            <label>Resolution</label>
            <div class="seg">
              <button v-for="[v, l] in RES_PRESETS" :key="v" type="button" :class="{ active: resPreset === v }" @click="resPreset = v">{{ l }}</button>
            </div>
          </div>

          <!-- reverse -->
          <div v-if="showReverse" class="toggle-row">
            <label>Keep audio (reversed)</label>
            <button class="switch" :class="{ on: keepAudio }" type="button" @click="keepAudio = !keepAudio"></button>
          </div>

          <!-- subtitle -->
          <template v-if="showSubtitle">
            <div class="ctl"><label>Caption text</label><div class="field"><input type="text" v-model="capText" /></div></div>
            <div class="ctl">
              <label>Position</label>
              <div class="seg">
                <button v-for="p in ['bottom', 'center', 'top']" :key="p" type="button" :class="{ active: capPos === p }" @click="capPos = p">{{ p[0].toUpperCase() + p.slice(1) }}</button>
              </div>
            </div>
          </template>

          <!-- merge clip list -->
          <template v-if="isMerge">
            <div class="ctl">
              <label>Clips to join</label>
              <div class="clip-list">
                <div v-for="(c, i) in clips" :key="c.url" class="clip-row" @click="selectClip(i)">
                  <span class="ci">{{ i + 1 }}</span><span class="cn">{{ c.name }}</span>
                  <span class="cx" @click.stop="removeClip(i)"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg></span>
                </div>
              </div>
            </div>
            <button class="btn btn-outline" type="button" @click="pick">Add more clips</button>
          </template>

          <!-- extract frames -->
          <template v-if="isFrames">
            <div class="ctl">
              <label>Capture every <span class="rangeval">{{ frameInterval }}s</span></label>
              <div class="rangewrap"><input type="range" min="0.5" max="10" step="0.5" v-model.number="frameInterval" /></div>
            </div>
            <div class="seg">
              <button type="button" :class="{ active: frameFmt === 'png' }" @click="frameFmt = 'png'">PNG</button>
              <button type="button" :class="{ active: frameFmt === 'jpg' }" @click="frameFmt = 'jpg'">JPG</button>
            </div>
          </template>

          <!-- GIF 控件 -->
          <template v-if="isGif">
            <div class="ctl">
              <label>Speed <span class="rangeval">{{ gfps }} fps</span></label>
              <div class="rangewrap"><input type="range" min="1" max="24" v-model.number="gfps" /></div>
            </div>
            <div class="ctl">
              <label>Output size</label>
              <div class="seg">
                <button v-for="[v, l] in GIF_SIZES" :key="v" type="button" :class="{ active: gsize === v }" @click="gsize = v">{{ l }}</button>
              </div>
            </div>
            <div class="ctl">
              <label>Play direction</label>
              <div class="seg">
                <button type="button" :class="{ active: gdir === 'fwd' }" @click="gdir = 'fwd'">Forward</button>
                <button type="button" :class="{ active: gdir === 'boomerang' }" @click="gdir = 'boomerang'">Boomerang</button>
              </div>
            </div>
            <div class="toggle-row"><label>Loop forever</label><button class="switch" :class="{ on: gloop }" type="button" @click="gloop = !gloop"></button></div>
          </template>

          <!-- mute / fade toggles -->
          <div v-if="showMuteSw" class="toggle-row">
            <label>Mute audio</label>
            <button class="switch" :class="{ on: muted }" type="button" @click="muted = !muted"></button>
          </div>
          <div v-if="showFadeSw" class="toggle-row">
            <label>Fade in / out</label>
            <button class="switch" :class="{ on: fade }" type="button" @click="fade = !fade"></button>
          </div>

          <!-- 导出格式（trim/crop） -->
          <div v-if="showFmtSeg" class="ctl">
            <label>Export format</label>
            <div class="seg">
              <button v-for="[v, l] in FMTS" :key="v" type="button" :class="{ active: fmt === v }" @click="fmt = v">{{ l }}</button>
            </div>
          </div>

          <p class="note">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" /></svg>
            <span v-if="isFrames">Frames are captured from the video right here in your browser, and your files are never stored.</span>
            <span v-else-if="isGif">Frames preview live on-device. Final GIF encoding runs on SharkFoto for crisp, optimized output.</span>
            <span v-else>Preview & edits run on-device. The final re-encode runs securely on SharkFoto — uploaded only when you export.</span>
          </p>

          <!-- 服务端处理中提示 -->
          <p v-if="serverBusy" class="proc">
            <span class="spin"></span>
            Processing on our servers… this can take a moment for larger files.
          </p>

          <!-- 产物就绪提示 -->
          <div v-if="resultShown" class="result-pill">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" /></svg>
            <span>Ready · <b>{{ (resultExt || "").toUpperCase() }}</b> · <b>{{ resultSize }}</b></span>
          </div>

          <div class="ws-actions">
            <!-- 抽帧/GIF 走原 exportAction；视频重活 + merge 产物就绪后主按钮变 Download -->
            <button
              v-if="resultShown && !isFrames && !isGif"
              class="btn btn-vid btn-lg"
              type="button"
              @click="onDownload"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:-3px;margin-right:7px"><path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" stroke-linecap="round" stroke-linejoin="round" /></svg>Download
            </button>
            <button
              v-else
              class="btn btn-vid btn-lg"
              type="button"
              :disabled="extracting || primaryDisabled"
              @click="exportAction"
            >{{ extracting ? "Extracting…" : primaryLabel }}</button>
            <button class="btn btn-outline" type="button" :disabled="serverBusy" @click="reset">{{ isGif ? "Start over" : "Use another video" }}</button>
          </div>
        </div>
      </div>

      <div class="trust">
        <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" /></svg>Private — on-device preview</span>
        <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h7l-1 8 10-12h-7z" /></svg>Fast &amp; secure</span>
        <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v10M7 12h10" /></svg>Unlimited &amp; free</span>
      </div>
    </div>

    <!-- toast -->
    <transition name="vt-toast">
      <div v-if="toast" class="toast"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg><span>{{ toast }}</span></div>
    </transition>
  </div>
</template>

<style scoped>
.vt { width: 100%; max-width: 860px; margin: 0 auto; --c-vid: #4ea2ff; }

/* dropzone */
.dz { border: 2px dashed var(--line-strong); border-radius: 20px; background: var(--surface); padding: 48px 24px; text-align: center; cursor: pointer; transition: 0.18s; }
.dz.drag, .dz:hover { border-color: var(--c-vid); background: linear-gradient(180deg, color-mix(in oklab, var(--c-vid) 8%, var(--surface)), var(--bg-2)); }
.dz-ico { width: 56px; height: 56px; margin: 0 auto 14px; color: var(--c-vid); }
.dz-ico svg { width: 100%; height: 100%; }
.dz h3 { font-size: 20px; font-weight: 700; }
.dz .hint { color: var(--tx-dim); font-size: 14px; margin-top: 6px; }
.dz .formats { margin: 12px 0; display: flex; gap: 8px; justify-content: center; flex-wrap: wrap; }
.dz .formats span { font-size: 12px; font-weight: 700; color: var(--tx-faint); border: 1px solid var(--line); border-radius: 7px; padding: 3px 10px; }
.dz-or { color: var(--tx-faint); font-size: 13px; margin: 10px 0 16px; }
.btn-vid { background: linear-gradient(135deg, #3b82f6, var(--c-vid)); color: #04121f; border: none; box-shadow: 0 8px 24px -8px rgba(78, 162, 255, 0.6); }
.btn-vid:hover { filter: brightness(1.06); }
.btn-vid:disabled { opacity: 0.6; cursor: default; filter: none; }

/* workspace */
.ws-grid { display: grid; grid-template-columns: 1fr 360px; gap: 22px; align-items: start; }
.ws-left { min-width: 0; }

/* video stage */
.vstage { position: relative; border-radius: 16px; overflow: hidden; border: 1px solid var(--line); background: #06070b; aspect-ratio: 16/9; display: grid; place-items: center; }
.video { width: 100%; height: 100%; object-fit: contain; display: block; background: #000; }
/* 暗层不吃指针事件；裁剪框与四个手柄 pointer-events:auto 才能拖。位置由 cropBoxStyle 经 :style 驱动。 */
.crop-ov { position: absolute; inset: 0; pointer-events: none; touch-action: none; }
.crop-box { position: absolute; border: 2px solid var(--c-vid); box-shadow: 0 0 0 9999px rgba(4, 8, 14, 0.45); pointer-events: auto; cursor: move; touch-action: none; box-sizing: border-box; }
.crop-box .hd { position: absolute; width: 14px; height: 14px; border: 2px solid var(--c-vid); background: #06070b; border-radius: 3px; pointer-events: auto; touch-action: none; }
.crop-box .tl { top: -8px; left: -8px; cursor: nwse-resize; } .crop-box .tr { top: -8px; right: -8px; cursor: nesw-resize; }
.crop-box .bl { bottom: -8px; left: -8px; cursor: nesw-resize; } .crop-box .br { bottom: -8px; right: -8px; cursor: nwse-resize; }
.crop-read { margin-top: 8px; font-size: 12px; font-weight: 600; color: #a9d2ff; font-variant-numeric: tabular-nums; }
.cap-overlay { position: absolute; left: 0; right: 0; text-align: center; pointer-events: none; padding: 0 16px; }
.cap { display: inline-block; background: rgba(0, 0, 0, 0.66); color: #fff; font-weight: 700; font-size: clamp(14px, 3.2vw, 22px); padding: 4px 12px; border-radius: 6px; line-height: 1.25; }

/* transport */
.transport { display: flex; align-items: center; gap: 12px; margin-top: 12px; }
.tp-btn { width: 40px; height: 40px; border-radius: 11px; border: 1px solid var(--line); background: var(--surface); color: var(--tx); cursor: pointer; display: grid; place-items: center; flex: none; }
.tp-btn:hover { border-color: var(--c-vid); color: #a9d2ff; }
.tp-btn svg { width: 20px; height: 20px; }
.tp-time { font-size: 13px; color: var(--tx-dim); font-variant-numeric: tabular-nums; }
.tp-time b { color: var(--tx); }
.tp-sel { font-size: 13px; color: #a9d2ff; font-weight: 600; }

/* timeline */
.timeline { margin-top: 14px; }
.tl-label { display: flex; justify-content: space-between; font-size: 12px; font-weight: 700; color: var(--tx-faint); margin-bottom: 8px; }
.filmstrip { position: relative; height: 56px; border-radius: 12px; border: 1px solid var(--line); background: repeating-linear-gradient(90deg, var(--surface-2) 0 14px, var(--surface) 14px 28px); overflow: hidden; user-select: none; touch-action: none; }
.tl-mask { position: absolute; top: 0; bottom: 0; left: 0; background: rgba(4, 8, 14, 0.62); }
.tl-mask.r { left: auto; right: 0; }
.tl-range { position: absolute; top: 0; bottom: 0; border: 2px solid var(--c-vid); border-radius: 8px; box-sizing: border-box; }
.tl-handle { position: absolute; top: 0; bottom: 0; width: 14px; background: var(--c-vid); cursor: ew-resize; display: grid; place-items: center; }
.tl-handle.l { left: -2px; border-radius: 8px 0 0 8px; } .tl-handle.r { right: -2px; border-radius: 0 8px 8px 0; }
.tl-handle::after { content: ""; width: 2px; height: 18px; background: #04121f; border-radius: 2px; }
.tl-playhead { position: absolute; top: -2px; bottom: -2px; width: 2px; background: #fff; box-shadow: 0 0 6px rgba(255, 255, 255, 0.8); pointer-events: none; }
.tl-times { display: flex; justify-content: space-between; font-size: 11px; color: var(--tx-faint); margin-top: 6px; font-variant-numeric: tabular-nums; }

/* GIF stage */
.gif-stage { position: relative; border-radius: 16px; overflow: hidden; border: 1px solid var(--line); background: #06070b; aspect-ratio: 4/3; display: grid; place-items: center; }
.gif-frame { max-width: 100%; max-height: 100%; object-fit: contain; }
.live-badge { position: absolute; top: 12px; left: 12px; display: inline-flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 800; letter-spacing: 0.05em; color: #8ff0cb; background: rgba(0, 0, 0, 0.5); padding: 4px 9px; border-radius: 7px; }
.live-badge .dot { width: 7px; height: 7px; border-radius: 50%; background: #34d399; box-shadow: 0 0 8px #34d399; }
.frames-bar { margin-top: 14px; }
.frames-lab { display: flex; justify-content: space-between; font-size: 12px; font-weight: 700; color: var(--tx-faint); margin-bottom: 8px; }
.frames-lab .clear { color: var(--c-vid); cursor: pointer; }
.frames { display: flex; gap: 8px; flex-wrap: wrap; }
.frame-thumb { position: relative; width: 64px; height: 64px; border-radius: 10px; overflow: hidden; border: 2px solid var(--line); cursor: grab; flex: none; }
.frame-thumb.cur { border-color: var(--c-vid); }
.frame-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.frame-thumb .idx { position: absolute; bottom: 2px; left: 4px; font-size: 11px; font-weight: 800; color: #fff; text-shadow: 0 1px 2px #000; }
.frame-thumb .del { position: absolute; top: 2px; right: 2px; width: 18px; height: 18px; border-radius: 50%; background: rgba(0, 0, 0, 0.6); color: #fff; display: grid; place-items: center; cursor: pointer; }
.frame-thumb .del svg { width: 11px; height: 11px; }
.frame-add { width: 64px; height: 64px; border-radius: 10px; border: 2px dashed var(--line-strong); color: var(--tx-faint); display: grid; place-items: center; cursor: pointer; flex: none; }
.frame-add svg { width: 22px; height: 22px; }
.frame-add:hover { border-color: var(--c-vid); color: var(--c-vid); }

/* panel */
.ws-panel { display: flex; flex-direction: column; gap: 16px; }
.ws-file { display: flex; align-items: center; gap: 10px; border: 1px solid var(--line); border-radius: 12px; padding: 12px 14px; background: var(--surface); }
.ws-file .thumb { width: 40px; height: 40px; border-radius: 9px; background: var(--surface-2); display: grid; place-items: center; color: var(--c-vid); flex: none; }
.ws-file .meta { flex: 1; min-width: 0; }
.ws-file .fn { font-weight: 600; font-size: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ws-file .fsz { font-size: 12px; color: var(--tx-faint); }
.ws-file .rm { background: none; border: none; color: var(--tx-faint); cursor: pointer; flex: none; }
.ctl label { font-size: 12px; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; color: var(--tx-faint); display: block; margin-bottom: 10px; }
.ctl .rangeval { color: #a9d2ff; float: right; text-transform: none; letter-spacing: 0; }
.field { display: flex; align-items: center; gap: 8px; background: var(--surface-2); border: 1px solid var(--line); border-radius: 11px; padding: 10px 12px; }
.field input { flex: 1; background: none; border: none; color: var(--tx); font-family: var(--font-h); font-weight: 600; font-size: 15px; outline: none; width: 100%; }
.field .unit { font-size: 12px; color: var(--tx-faint); flex: none; }
.rangewrap { display: flex; align-items: center; }
.rangewrap input[type="range"] { width: 100%; accent-color: var(--c-vid); }
.seg { display: flex; gap: 6px; flex-wrap: wrap; }
.seg button { flex: 1; min-width: 54px; background: var(--surface-2); border: 1px solid var(--line); border-radius: 10px; padding: 9px 6px; color: var(--tx-dim); font-family: var(--font-b); font-weight: 700; font-size: 13px; cursor: pointer; }
.seg button.active { background: color-mix(in oklab, var(--c-vid) 16%, transparent); border-color: var(--c-vid); color: #a9d2ff; }
.toggle-row { display: flex; align-items: center; justify-content: space-between; }
.toggle-row label { font-weight: 700; color: var(--tx-dim); font-size: 13px; }
.switch { width: 42px; height: 24px; border-radius: 999px; background: var(--surface-2); border: 1px solid var(--line); position: relative; cursor: pointer; flex: none; transition: 0.15s; padding: 0; }
.switch::after { content: ""; position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; border-radius: 50%; background: var(--tx-faint); transition: 0.15s; }
.switch.on { background: color-mix(in oklab, var(--c-vid) 30%, transparent); border-color: var(--c-vid); }
.switch.on::after { left: 21px; background: var(--c-vid); }
.clip-list { display: flex; flex-direction: column; gap: 6px; }
.clip-row { display: flex; align-items: center; gap: 10px; background: var(--surface-2); border: 1px solid var(--line); border-radius: 10px; padding: 8px 10px; cursor: pointer; }
.clip-row .ci { width: 20px; height: 20px; border-radius: 6px; background: color-mix(in oklab, var(--c-vid) 18%, transparent); color: #a9d2ff; font-size: 11px; font-weight: 800; display: grid; place-items: center; flex: none; }
.clip-row .cn { flex: 1; font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.clip-row .cx { color: var(--tx-faint); cursor: pointer; flex: none; }
.clip-row .cx svg { width: 14px; height: 14px; display: block; }
.note { display: flex; gap: 10px; font-size: 13px; color: var(--tx-dim); line-height: 1.5; background: color-mix(in oklab, var(--c-vid) 8%, var(--surface)); border: 1px solid color-mix(in oklab, var(--c-vid) 25%, var(--line)); border-radius: 12px; padding: 12px 14px; }
.note svg { width: 18px; height: 18px; color: var(--c-vid); flex: none; margin-top: 1px; }
.ws-actions { display: flex; flex-direction: column; gap: 10px; }

/* 服务端处理中 */
.proc { display: flex; align-items: center; gap: 10px; font-size: 13px; color: var(--tx-dim); line-height: 1.5; }
.spin { width: 16px; height: 16px; flex: none; border-radius: 50%; border: 2px solid color-mix(in oklab, var(--c-vid) 35%, transparent); border-top-color: var(--c-vid); animation: vt-spin 0.8s linear infinite; }
@keyframes vt-spin { to { transform: rotate(360deg); } }

/* 产物就绪 pill */
.result-pill { display: flex; align-items: center; gap: 9px; font-size: 13px; color: var(--tx); background: color-mix(in oklab, #34d399 12%, var(--surface)); border: 1px solid color-mix(in oklab, #34d399 35%, var(--line)); border-radius: 12px; padding: 11px 14px; }
.result-pill svg { width: 18px; height: 18px; color: #34d399; flex: none; }
.result-pill b { color: var(--tx); }

/* trust */
.trust { display: flex; gap: 22px; justify-content: center; flex-wrap: wrap; margin-top: 20px; }
.trust .i { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 600; color: var(--tx-dim); }
.trust .i svg { width: 16px; height: 16px; color: var(--c-vid); }

/* toast */
.toast { position: fixed; bottom: 26px; left: 50%; transform: translateX(-50%); background: var(--surface); border: 1px solid var(--c-vid); color: var(--tx); padding: 13px 20px; border-radius: 12px; font-weight: 600; font-size: 14px; box-shadow: 0 18px 50px -12px rgba(0, 0, 0, 0.8); z-index: 120; display: inline-flex; align-items: center; gap: 10px; max-width: 90vw; }
.toast svg { width: 18px; height: 18px; color: var(--c-vid); flex: none; }
.vt-toast-enter-active, .vt-toast-leave-active { transition: 0.25s; }
.vt-toast-enter-from, .vt-toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(20px); }

@media (max-width: 760px) { .ws-grid { grid-template-columns: 1fr; } }
</style>
